import React, { useState, useEffect } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "../lib/AxiosGo";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const SendMessagePage = () => {
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState(() => {
    try {
      const saved = localStorage.getItem("wabot_send_history");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [formData, setFormData] = useState({
    deviceId: "",
    number: "",
    message: ""
  });

  // Fetch all devices of the user
  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      const res = await axios.get("/devices/data");
      const list = res.data || [];
      setDevices(list);

      // Otomatis pilih device yang statusnya 'connected' pertama
      const activeDevice = list.find((d) => d.status === "connected");
      if (activeDevice) {
        setFormData((prev) => ({ ...prev, deviceId: activeDevice.device_id }));
      } else if (list.length > 0) {
        setFormData((prev) => ({ ...prev, deviceId: list[0].device_id }));
      }
    } catch (err) {
      console.error("Gagal mengambil data device:", err);
      toast.error("Gagal memuat daftar perangkat WhatsApp");
    } finally {
      setLoadingDevices(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInsertTemplate = (templateText) => {
    setFormData((prev) => ({
      ...prev,
      message: prev.message ? `${prev.message}\n${templateText}` : templateText
    }));
  };

  const handleInsertEmoji = (emoji) => {
    setFormData((prev) => ({
      ...prev,
      message: `${prev.message}${emoji}`
    }));
  };

  const handleSend = async (e) => {
    e.preventDefault();

    if (!formData.deviceId) {
      toast.warning("Silakan pilih perangkat WhatsApp terlebih dahulu!");
      return;
    }

    if (!formData.number.trim()) {
      toast.warning("Nomor WhatsApp tujuan tidak boleh kosong!");
      return;
    }

    if (!formData.message.trim()) {
      toast.warning("Isi pesan WhatsApp tidak boleh kosong!");
      return;
    }

    const selectedDev = devices.find((d) => d.device_id === formData.deviceId);
    if (selectedDev && selectedDev.status !== "connected") {
      toast.error(`Perangkat "${selectedDev.device_name}" sedang offline. Silakan connect / scan QR terlebih dahulu di menu Device.`);
      return;
    }

    setSending(true);
    try {
      const res = await axios.post("/whatsapp/send", {
        deviceId: formData.deviceId,
        number: formData.number.trim(),
        message: formData.message.trim()
      });

      toast.success(res.data?.message || "✅ Pesan WhatsApp berhasil terkirim!");

      // Tambahkan ke riwayat
      const newEntry = {
        id: Date.now(),
        deviceName: selectedDev?.device_name || formData.deviceId,
        number: formData.number.trim(),
        message: formData.message.trim(),
        time: new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
        status: "success"
      };

      const updatedHistory = [newEntry, ...history.slice(0, 19)];
      setHistory(updatedHistory);
      try {
        localStorage.setItem("wabot_send_history", JSON.stringify(updatedHistory));
      } catch {}

      // Reset text pesan
      setFormData((prev) => ({ ...prev, message: "" }));
    } catch (err) {
      console.error("Error sending WA message:", err);
      const errMsg = err.response?.data?.error || err.response?.data?.message || "Gagal mengirim pesan WhatsApp.";
      toast.error(`❌ ${errMsg}`);

      const failedEntry = {
        id: Date.now(),
        deviceName: selectedDev?.device_name || formData.deviceId,
        number: formData.number.trim(),
        message: formData.message.trim(),
        time: new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
        status: "failed"
      };
      const updatedHistory = [failedEntry, ...history.slice(0, 19)];
      setHistory(updatedHistory);
    } finally {
      setSending(false);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm("Hapus seluruh riwayat pengiriman pesan?")) {
      setHistory([]);
      localStorage.removeItem("wabot_send_history");
    }
  };

  const selectedDeviceObj = devices.find((d) => d.device_id === formData.deviceId);

  return (
    <MasterLayout>
      <Breadcrumb title="Kirim Pesan WhatsApp Langsung" />

      <div className="row g-4">
        {/* Kolom Kiri: Form Pengiriman Pesan */}
        <div className="col-xl-7 col-lg-7">
          <div className="card radius-12 border-0 shadow-sm h-100">
            <div className="card-header py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <Icon icon="mdi:send-circle" className="text-primary text-2xl" />
                <h6 className="text-lg fw-semibold mb-0">Formulir Kirim Pesan WhatsApp</h6>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                onClick={fetchDevices}
                disabled={loadingDevices}
              >
                <Icon icon="solar:refresh-linear" className={loadingDevices ? "spin text-md" : "text-md"} />
                <span>Refresh Device</span>
              </button>
            </div>

            <div className="card-body p-24">
              <form onSubmit={handleSend}>
                {/* 1. Pilih Device */}
                <div className="mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Pilih Perangkat Pengirim (Device WA) <span className="text-danger">*</span>
                  </label>
                  {loadingDevices ? (
                    <div className="p-12 text-center text-secondary text-sm">
                      <Icon icon="svg-spinners:ring-resize" className="me-2" /> Memuat daftar device...
                    </div>
                  ) : devices.length === 0 ? (
                    <div className="alert alert-warning py-12 px-16 text-sm mb-0">
                      <Icon icon="solar:danger-triangle-linear" className="me-2 text-lg" />
                      Belum ada perangkat WhatsApp.{" "}
                      <Link to="/devices" className="text-primary fw-bold text-decoration-underline">
                        Tambah Perangkat di sini &rarr;
                      </Link>
                    </div>
                  ) : (
                    <div>
                      <select
                        name="deviceId"
                        className="form-select radius-8 py-10 px-14"
                        value={formData.deviceId}
                        onChange={handleChange}
                        required
                      >
                        <option value="">-- Pilih Perangkat Pengirim --</option>
                        {devices.map((dev) => (
                          <option key={dev.device_id} value={dev.device_id}>
                            {dev.device_name || "Tanpa Nama"} ({dev.device_number || dev.device_id}) — [
                            {dev.status === "connected" ? "🟢 CONNECTED" : "🔴 DISCONNECTED"}]
                          </option>
                        ))}
                      </select>

                      {/* Status info box */}
                      {selectedDeviceObj && (
                        <div
                          className={`mt-10 p-10 radius-8 text-xs d-flex align-items-center justify-content-between ${
                            selectedDeviceObj.status === "connected"
                              ? "bg-success-focus text-success-main border border-success-subtle"
                              : "bg-danger-focus text-danger-main border border-danger-subtle"
                          }`}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <Icon
                              icon={
                                selectedDeviceObj.status === "connected"
                                  ? "solar:check-circle-bold"
                                  : "solar:close-circle-bold"
                              }
                              className="text-base"
                            />
                            <span>
                              Device: <strong>{selectedDeviceObj.device_name}</strong> | Status:{" "}
                              <strong>{selectedDeviceObj.status.toUpperCase()}</strong>
                            </span>
                          </div>
                          {selectedDeviceObj.status !== "connected" && (
                            <Link to="/devices" className="btn btn-danger btn-xs py-2 px-8 radius-4 text-xs">
                              Scan QR
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 2. Nomor WhatsApp Tujuan */}
                <div className="mb-20">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Nomor WhatsApp Tujuan <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-secondary border-end-0">
                      <Icon icon="solar:phone-calling-linear" className="text-lg" />
                    </span>
                    <input
                      type="text"
                      name="number"
                      className="form-control radius-8 border-start-0 py-10 px-14"
                      placeholder="Contoh: 08123456789 atau 628123456789"
                      value={formData.number}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-text text-xs text-secondary mt-4">
                    Format nomor dapat menggunakan awalan <code>08...</code>, <code>628...</code>, atau <code>+62...</code>.
                  </div>
                </div>

                {/* 3. Isi Pesan */}
                <div className="mb-20">
                  <div className="d-flex align-items-center justify-content-between mb-8">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-0">
                      Isi Pesan WhatsApp <span className="text-danger">*</span>
                    </label>
                    <span className="text-xs text-secondary">
                      {formData.message.length} Karakter
                    </span>
                  </div>

                  <textarea
                    name="message"
                    rows="6"
                    className="form-control radius-8 py-10 px-14"
                    placeholder="Ketik pesan Anda di sini... (Mendukung format WhatsApp: *tebal*, _miring_, ~coret~)"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>

                  {/* Tombol Cepat Emoji */}
                  <div className="d-flex flex-wrap align-items-center gap-1 mt-8">
                    <span className="text-xs text-secondary me-2">Emoji Cepat:</span>
                    {["👋", "👮‍♂️", "📋", "✅", "⚠️", "📞", "📍", "🙏", "🚨", "📌"].map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="btn btn-light btn-sm py-1 px-8 text-sm"
                        onClick={() => handleInsertEmoji(emoji)}
                        title={`Sisipkan ${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>

                  {/* Template Pesan Cepat */}
                  <div className="mt-14 p-12 bg-light radius-8 border">
                    <div className="text-xs fw-semibold text-primary mb-6 d-flex align-items-center gap-1">
                      <Icon icon="solar:notes-linear" /> Template Pesan Cepat:
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-xs py-4 px-8 text-xs radius-4"
                        onClick={() =>
                          handleInsertTemplate(
                            "Yth. Bapak/Ibu,\n\nTerima kasih telah menghubungi Layanan Pengaduan Subdit IV Renakta Polda Jateng.\n\nAda yang dapat kami bantu?"
                          )
                        }
                      >
                        + Salam Pembuka Layanan
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-success btn-xs py-4 px-8 text-xs radius-4"
                        onClick={() =>
                          handleInsertTemplate(
                            "Pemberitahuan: Laporan pengaduan Anda telah kami terima dan saat ini sedang ditindaklanjuti oleh petugas piket Subdit Renakta Polda Jateng. Terima kasih."
                          )
                        }
                      >
                        + Update Status Laporan
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-info btn-xs py-4 px-8 text-xs radius-4"
                        onClick={() =>
                          handleInsertTemplate(
                            "Mohon untuk mengirimkan bukti pendukung tambahan (foto/dokumen/lokasi) guna melengkapi berkas tindak lanjut laporan Anda."
                          )
                        }
                      >
                        + Permintaan Bukti Tambahan
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tombol Submit & Reset */}
                <div className="d-flex align-items-center gap-3 mt-24">
                  <button
                    type="submit"
                    className="btn btn-primary px-24 py-11 d-flex align-items-center gap-2 radius-8"
                    disabled={sending || devices.length === 0}
                  >
                    {sending ? (
                      <>
                        <Icon icon="svg-spinners:ring-resize" className="text-lg" />
                        <span>Mengirim Pesan...</span>
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:send" className="text-lg" />
                        <span>Kirim Pesan WhatsApp</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary px-16 py-11 radius-8"
                    onClick={() => setFormData({ ...formData, number: "", message: "" })}
                    disabled={sending}
                  >
                    Reset Form
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Kolom Kanan: Live WhatsApp Chat Preview */}
        <div className="col-xl-5 col-lg-5">
          <div className="card radius-12 border-0 shadow-sm mb-24">
            <div className="card-header py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <Icon icon="solar:smartphone-linear" className="text-success text-2xl" />
                <h6 className="text-lg fw-semibold mb-0">Live Preview WhatsApp</h6>
              </div>
              <span className="badge bg-success-subtle text-success-main border border-success-subtle px-10 py-4 text-xs">
                Real-Time Preview
              </span>
            </div>

            <div className="card-body p-0">
              {/* WhatsApp Smartphone Mockup Container */}
              <div
                style={{
                  backgroundColor: "#E5DDD5",
                  backgroundImage: "radial-gradient(#d1c7bc 1px, transparent 1px)",
                  backgroundSize: "16px 16px",
                  minHeight: "360px",
                  borderRadius: "0 0 12px 12px",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                {/* WA Top Bar Mockup */}
                <div
                  className="py-10 px-16 d-flex align-items-center justify-content-between text-white"
                  style={{ backgroundColor: "#075E54" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center bg-white text-primary"
                      style={{ width: "36px", height: "36px", fontWeight: "bold", fontSize: "14px" }}
                    >
                      👮‍♂️
                    </div>
                    <div>
                      <div className="fw-bold text-sm text-white leading-1">
                        {selectedDeviceObj?.device_name || "Layanan Pengaduan POLDA"}
                      </div>
                      <div className="text-xs text-white-50">Online</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 text-white">
                    <Icon icon="solar:phone-bold" className="text-lg" />
                    <Icon icon="solar:menu-dots-bold" className="text-lg" />
                  </div>
                </div>

                {/* WA Chat Body */}
                <div className="p-16 flex-grow-1 d-flex flex-column justify-content-end">
                  {/* Info Nomor Penerima */}
                  <div className="text-center mb-16">
                    <span
                      className="bg-white shadow-xs px-12 py-4 radius-6 text-xs text-secondary"
                      style={{ opacity: 0.9 }}
                    >
                      Penerima: <strong>{formData.number || "08xxx"}</strong>
                    </span>
                  </div>

                  {/* Chat Bubble Outgoing */}
                  <div className="d-flex justify-content-end">
                    <div
                      className="shadow-sm p-12 radius-8 position-relative"
                      style={{
                        backgroundColor: "#DCF8C6",
                        maxWidth: "85%",
                        minWidth: "160px",
                        wordBreak: "break-word"
                      }}
                    >
                      <div
                        className="text-sm text-dark mb-4"
                        style={{ whiteSpace: "pre-wrap", lineHeight: "1.4" }}
                      >
                        {formData.message || (
                          <span className="text-muted fst-italic text-xs">
                            Pesan yang Anda ketik di formulir sebelah kiri akan tampil secara langsung di sini...
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center justify-content-end gap-1 mt-4">
                        <span style={{ fontSize: "10px", color: "#667781" }}>
                          {new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <Icon icon="solar:check-read-bold" style={{ fontSize: "13px", color: "#53bdeb" }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Riwayat Pengiriman Sesi Ini */}
          <div className="card radius-12 border-0 shadow-sm">
            <div className="card-header py-14 px-20 bg-base border border-end-0 border-start-0 border-top-0 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <Icon icon="solar:history-linear" className="text-secondary text-xl" />
                <h6 className="text-md fw-semibold mb-0">Riwayat Pengiriman Terbaru</h6>
              </div>
              {history.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-xs py-2 px-8 radius-4 text-xs"
                  onClick={handleClearHistory}
                >
                  Bersihkan
                </button>
              )}
            </div>

            <div className="card-body p-0">
              {history.length === 0 ? (
                <div className="text-center py-24 text-secondary text-sm">
                  <Icon icon="solar:chat-round-line-linear" className="text-3xl mb-4 d-block mx-auto text-muted" />
                  Belum ada riwayat pengiriman pesan.
                </div>
              ) : (
                <div className="list-group list-group-flush" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {history.map((item) => (
                    <div key={item.id} className="list-group-item p-12 border-bottom">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`badge ${
                              item.status === "success"
                                ? "bg-success-subtle text-success-main"
                                : "bg-danger-subtle text-danger-main"
                            } text-xs px-6 py-2`}
                          >
                            {item.status === "success" ? "Terkirim" : "Gagal"}
                          </span>
                          <strong className="text-sm text-dark">{item.number}</strong>
                        </div>
                        <span className="text-xs text-secondary">{item.time}</span>
                      </div>
                      <p
                        className="text-xs text-secondary mb-0 text-truncate"
                        style={{ maxWidth: "320px" }}
                        title={item.message}
                      >
                        {item.message}
                      </p>
                      <div className="text-xs text-muted mt-2">
                        Device: <em>{item.deviceName}</em>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default SendMessagePage;
